import { useState } from 'react';
import Draggable from 'react-draggable';
import { X, Send, GripHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TiptapEditor } from './TiptapEditor';

export interface NonComplianceMessage {
  itemCode: string | null;
  description: string;
  messages: string[];
}

interface VendorNotificationPopoutProps {
  open: boolean;
  onClose: () => void;
  vendorName: string;
  vendorPhone: string | null;
  vendorEmail?: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  invoiceTotal: number;
  currency: string | null;
  userEmail: string;
  companyEmail: string | null;
  nonComplianceMessages: NonComplianceMessage[];
}

function buildEmailBody(messages: NonComplianceMessage[]): string {
  return messages
    .map((item) => {
      const heading = item.itemCode
        ? `<strong>${item.itemCode} — ${item.description}</strong>`
        : `<strong>${item.description}</strong>`;
      const bullets = item.messages.map((m) => `<li>- ${m}</li>`).join('');
      return `<p>${heading}</p><ul>${bullets}</ul></p>`;
    })
    .join('');
}

function buildTextBody(messages: NonComplianceMessage[]): string {
  return messages
    .map((item) => {
      const heading = item.itemCode
        ? `${item.itemCode} — ${item.description}`
        : item.description;
      const lines = item.messages.map((m) => `  - ${m}`).join('\n');
      return `${heading}\n${lines}`;
    })
    .join('\n\n');
}

function formatCurrency(amount: number, currency: string | null): string {
  const code = currency || 'USD';
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 2,
  });
}

export function VendorNotificationPopout({
  open,
  onClose,
  vendorName,
  vendorPhone,
  vendorEmail,
  invoiceNumber,
  invoiceDate,
  invoiceTotal,
  currency,
  userEmail,
  companyEmail,
  nonComplianceMessages,
}: VendorNotificationPopoutProps) {
  const [mode, setMode] = useState<string>('email');

  // Email state
  const [fromSelection, setFromSelection] = useState<string>('user');
  const [customFrom, setCustomFrom] = useState('');
  const [emailTo, setEmailTo] = useState(vendorEmail || '');
  const [emailSubject, setEmailSubject] = useState(
    `Non-compliance Notice for Invoice ${invoiceNumber || ''}`
  );
  const [emailBody, setEmailBody] = useState(() => buildEmailBody(nonComplianceMessages));

  // Text state
  const [phone, setPhone] = useState(vendorPhone || '');
  const [textBody, setTextBody] = useState(() => buildTextBody(nonComplianceMessages));

  if (!open) return null;

  const getFromEmail = () => {
    if (fromSelection === 'user') return userEmail;
    if (fromSelection === 'company') return companyEmail || '';
    return customFrom;
  };

  const handleSend = () => {
    window.alert('Message sent.');
    onClose();
  };

  const fromOptions: { value: string; label: string }[] = [
    { value: 'user', label: userEmail },
  ];
  if (companyEmail) {
    fromOptions.push({ value: 'company', label: companyEmail });
  }
  fromOptions.push({ value: 'custom', label: 'Custom...' });

  return (
    <Draggable handle=".drag-handle" bounds="parent">
      <div
        className="fixed z-50 bg-card border border-border rounded-lg shadow-2xl flex flex-col"
        style={{
          top: '10%',
          left: '30%',
          width: '40%',
          maxHeight: '80vh',
        }}
      >
        {/* Drag handle / header */}
        <div className="drag-handle flex items-center justify-between px-4 py-3 border-b border-border bg-muted/40 rounded-t-lg cursor-move select-none">
          <div className="flex items-center gap-2">
            <GripHorizontal className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-semibold">Vendor Notification</span>
          </div>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Read-only header */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">Vendor:</span>{' '}
              <span className="font-medium">{vendorName}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Invoice #:</span>{' '}
              <span className="font-medium">{invoiceNumber || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Invoice Date:</span>{' '}
              <span className="font-medium">{invoiceDate || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Total:</span>{' '}
              <span className="font-medium">{formatCurrency(invoiceTotal, currency)}</span>
            </div>
          </div>

          {/* Mode tabs */}
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="w-full">
              <TabsTrigger value="email" className="flex-1">
                Email
              </TabsTrigger>
              <TabsTrigger value="text" className="flex-1">
                Text Message
              </TabsTrigger>
            </TabsList>

            {/* Email mode */}
            <TabsContent value="email" className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="email-from" className="text-xs">
                    From
                  </Label>
                  <Select value={fromSelection} onValueChange={setFromSelection}>
                    <SelectTrigger id="email-from">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {fromOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fromSelection === 'custom' && (
                    <Input
                      placeholder="Enter email address"
                      value={customFrom}
                      onChange={(e) => setCustomFrom(e.target.value)}
                      className="mt-1.5"
                    />
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email-to" className="text-xs">
                    To
                  </Label>
                  <Input
                    id="email-to"
                    placeholder="Recipient email"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email-subject" className="text-xs">
                  Subject
                </Label>
                <Input
                  id="email-subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Body</Label>
                <TiptapEditor content={emailBody} onChange={setEmailBody} />
              </div>
            </TabsContent>

            {/* Text message mode */}
            <TabsContent value="text" className="space-y-3 mt-3">
              <div className="space-y-1.5">
                <Label htmlFor="sms-phone" className="text-xs">
                  Phone Number
                </Label>
                <Input
                  id="sms-phone"
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="sms-body" className="text-xs">
                  Message
                </Label>
                <Textarea
                  id="sms-body"
                  rows={8}
                  value={textBody}
                  onChange={(e) => setTextBody(e.target.value)}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSend}>
            <Send className="w-3.5 h-3.5 mr-1.5" />
            Send
          </Button>
        </div>
      </div>
    </Draggable>
  );
}
